"use client";

import { useEffect, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";

const STORAGE_KEY = "pauta:contentZoom";
const MIN = 80;
const MAX = 160;
const STEP = 10;

/**
 * Reading-zoom for content surfaces (Docs, IssueDetail).
 *
 * Why: agents tend to write dense markdown; humans want to scale just
 * the body without touching the whole UI density. We scale the wrapper
 * containing markdown render + comments via inline `fontSize` so every
 * downstream `text-[12.5px]` etc. compounds correctly.
 *
 * Stored as a single workspace-agnostic preference in localStorage so a
 * user who likes 130% gets it everywhere, and zoom doesn't reset on
 * navigation.
 */
export function useContentZoom(): {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  setZoom: (n: number) => void;
} {
  const [zoom, setZoomState] = useState(100);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= MIN && n <= MAX) setZoomState(n);
    }
    const onKey = (e: KeyboardEvent) => {
      // Cmd/Ctrl + 0 → reset zoom (matches browser default)
      if ((e.metaKey || e.ctrlKey) && e.key === "0") {
        const target = e.target as HTMLElement | null;
        if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;
        e.preventDefault();
        applyZoom(100, setZoomState);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return {
    zoom,
    zoomIn: () => applyZoom(Math.min(MAX, zoom + STEP), setZoomState),
    zoomOut: () => applyZoom(Math.max(MIN, zoom - STEP), setZoomState),
    reset: () => applyZoom(100, setZoomState),
    setZoom: (n: number) =>
      applyZoom(Math.max(MIN, Math.min(MAX, Math.round(n / STEP) * STEP)), setZoomState),
  };
}

function applyZoom(n: number, setZoomState: (v: number) => void) {
  setZoomState(n);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, String(n));
  }
}

/**
 * Compact toolbar with `−`, `100%` label (click to reset), `+`. Drop
 * into any content-detail header. Doesn't manage state — pass the
 * useContentZoom() bag in.
 */
export function ZoomControls({
  zoom,
  zoomIn,
  zoomOut,
  reset,
}: {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] overflow-hidden">
      <button
        onClick={zoomOut}
        disabled={zoom <= MIN}
        className="px-1.5 py-1 text-[var(--fg-tertiary)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        title="Zoom out"
      >
        <Minus size={11} />
      </button>
      <button
        onClick={reset}
        className="px-1.5 py-1 text-[10.5px] font-mono text-[var(--fg-tertiary)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)] border-x border-[var(--border)] transition-colors min-w-[42px]"
        title="Reset to 100% (Cmd+0)"
      >
        {zoom}%
      </button>
      <button
        onClick={zoomIn}
        disabled={zoom >= MAX}
        className="px-1.5 py-1 text-[var(--fg-tertiary)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        title="Zoom in"
      >
        <Plus size={11} />
      </button>
    </div>
  );
}

/**
 * Helper: returns the inline style that applies the zoom factor to a
 * wrapper. Use as `<div style={zoomStyle(zoom)}>...content...</div>`.
 * 100% = base 14px effective (browser default for em scaling).
 */
export function zoomStyle(zoom: number): React.CSSProperties {
  return {
    fontSize: `${(zoom / 100) * 14}px`,
    lineHeight: 1.55,
  };
}

// Re-export the icon for headers that want to compose differently
export { RotateCcw as ResetZoomIcon };
